import { enGB } from 'date-fns/locale'
import {
  format,
  isValid,
  addWeeks,
  isWithinInterval,
  startOfWeek,
  subWeeks
} from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'

const getOrdinalSuffix = (day: number) => {
  const j = day % 10
  const k = day % 100
  if (j === 1 && k !== 11) {
    return `${day}st`
  }
  if (j === 2 && k !== 12) {
    return `${day}nd`
  }
  if (j === 3 && k !== 13) {
    return `${day}rd`
  }
  return `${day}th`
}

const LONDON = 'Europe/London'

/**
 * The London calendar day of an instant, as a Date at midnight in the *browser's* zone.
 *
 * Date pickers read a Date's local calendar fields, so handing them a raw UTC instant makes the
 * day they show depend on where the browser is: 2026-11-11T23:30Z is the 11th in London and the
 * 12th in Amsterdam. Anchoring to the London day first means every browser shows the day the job
 * actually runs.
 */
export const londonDayAsLocalDate = (date: string | Date): Date => {
  const [year, month, day] = formatInTimeZone(new Date(date), LONDON, 'yyyy-MM-dd').split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Builds the stored instant from a picked day and a wall-clock time, resolved in London.
 *
 * The day is read from the Date's local calendar fields - what the picker displayed - and the time
 * is the string the user typed. Neither carries a zone, so the result is the same instant whatever
 * the browser's timezone, and it stays correct across BST/GMT. Using `setHours` here instead would
 * resolve the time in the browser's zone, which is how a client in Amsterdam ends up submitting
 * every date an hour early.
 */
export const combineDayAndTimeInLondon = (day: Date, time: string): Date => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  const pad = (n: number) => `${n}`.padStart(2, '0')
  const datePart = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
  return fromZonedTime(`${datePart} ${pad(hours)}:${pad(minutes)}:00`, LONDON)
}

export const formatDate = (date: string | Date, dateFormat = 'P') => {
  const newDate = typeof date === 'string' ? new Date(date) : date
  return isValid(newDate) ? format(newDate, dateFormat, { locale: enGB }) : 'n/a'
}

export const formatDateForPrinting = (date: Date) => {
  const dayWithOrdinal = getOrdinalSuffix(date.getDate())
  return `${format(date, 'eeee')} ${dayWithOrdinal} ${format(date, 'MMMM yyyy')}`
}

export const formatFriendlyShortDate = (date: string | Date) => {
  const newDate = typeof date === 'string' ? new Date(date) : date
  return format(newDate, 'dd/MMM/yyyy')
}

export const formatFriendlyShortDateTime = (date: string | Date) => {
  return formatDate(date, 'dd/MMM/yyyy HH:mma')
}

export const formatTime = (date: string | Date, timeFormat = 'HH:mm:ss') => {
  const newDate = typeof date === 'string' ? new Date(date) : date
  return isValid(newDate) ? format(newDate, timeFormat, { locale: enGB }) : 'n/a'
}

export const formatTimeUtc = (date: string, timeFormat = 'HH:mm:ss') => {
  const newDate = new Date(date)
  return isValid(newDate) ? format(newDate, timeFormat, { locale: enGB }) : 'n/a'
}

export const formatLocalTime = (date: string | Date, timeFormat = 'HH:mma') => {
  const timeZone = 'Europe/London'
  const zonedDate = toZonedTime(date, timeZone)
  return isValid(zonedDate) ? format(zonedDate, timeFormat, { locale: enGB }) : 'n/a'
}

export const mapTimestampToHour = (date: Date): number => {
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const formattedHours = hours < 10 ? `0${hours}` : `${hours}`
  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`
  return Number.parseInt(`${formattedHours}${formattedMinutes}`, 10)
}

export const timestampToNumber = (timestamp: string): number => {
  const [hours, minutes] = timestamp.split(':').map(Number)
  return hours * 100 + minutes
}

export const numberToTimestamp = (num: number): string => {
  const hours = Math.floor(num / 100)
  const minutes = num % 100
  const paddedHours = String(hours).padStart(2, '0')
  const paddedMinutes = String(minutes).padStart(2, '0')
  return `${paddedHours}:${paddedMinutes}`
}

export const addHoursToTimestamp = (timestamp: number, additionalHours: number): number => {
  let hours = Math.floor(timestamp / 100)
  const minutes = timestamp % 100
  hours += additionalHours
  hours %= 24
  return hours * 100 + minutes
}

export const formatLocalDate = (date: string | Date): string => {
  const timeZone = 'Europe/London'
  const zonedDate = toZonedTime(date, timeZone)
  return format(zonedDate, 'dd/MM/yyyy', { locale: enGB })
}

export const toLocalDate = (date: string | Date): Date => {
  const timeZone = 'Europe/London'
  return toZonedTime(date, timeZone)
}

export const formatLocalDateWithPattern = (date: string | Date, datePattern: string) => {
  const timeZone = 'Europe/London'
  const zonedDate = toZonedTime(date, timeZone)
  return format(zonedDate, datePattern, { locale: enGB })
}

export const isLastWeek = (date: Date, weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1) => {
  const startOfLastWeek = subWeeks(startOfWeek(new Date(), { weekStartsOn }), 1)
  const endOfLastWeek = addWeeks(startOfLastWeek, 1)
  return isWithinInterval(date, { start: startOfLastWeek, end: endOfLastWeek })
}
